/**
 * useUserSearch hook helper
 * Interface with user profile REST API
 *
 * Author: Relevant Bits
 */

import { useContext } from 'react';

import { useQuery } from '@tanstack/react-query';
import z from 'zod';

import { getAxiosInstance } from './axiosCache';
import { ConfigContext } from '../aurr/contexts/ConfigContext';
import { cacheTTLs } from '../common/constants';
import { logZodError } from '../helpers/log-zod-error';

const userProfileSchema = z.object({
  encryptedId: z.string(),
  aboutMe: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  userName: z.string(),
  profileImages: z
    .object({
      sizeX160: z.string(),
      sizeX120: z.string(),
      sizeX80: z.string(),
    })
    .default({
      sizeX80: '',
      sizeX120: '',
      sizeX160: '',
    }),
});

type UserProfileSchema = z.infer<typeof userProfileSchema>;

interface UseUserProfileProps {
  oxygenId: string;
  userProfileURL?: string;
  userContentURL?: string;
  bypassCache?: boolean;
  enabled?: boolean;
}

export const useUserProfile = ({
  oxygenId,
  bypassCache = false,
  userProfileURL,
  enabled = true,
}: UseUserProfileProps) => {
  // create axios client used in all community function requests
  const axiosConfig = {
    headers: {
      'Content-Type': 'application/vnd.api+json',
      Accept: 'application/vnd.api+json',
      ...(bypassCache && {
        Pragma: 'no-cache',
        'Cache-Control': 'no-store, max-age=0',
      }),
    },
    ...(!bypassCache && {
      cache: {
        ttl: cacheTTLs.userProfile,
      },
    }),
  };
  const { USER_PROFILE_URL } = useContext(ConfigContext);

  const api = getAxiosInstance(bypassCache);

  const userProfileConfig = {
    ...axiosConfig,
    baseURL: userProfileURL || USER_PROFILE_URL,
  };

  // Check if oxygenId matches the pattern to prevent API call
  const shouldSkipQuery = oxygenId?.includes('urn:adsk.content:content:');

  const {
    isLoading,
    error,
    data = {},
  } = useQuery({
    queryKey: [`userProfile-v2-${oxygenId}`],
    queryFn: () =>
      api
        .get(`/services/v2/users/${oxygenId}/ltc`, userProfileConfig)
        .then(res => {
          logZodError(userProfileSchema.safeParse(res.data));

          return res.data;
        }),
    enabled: !!oxygenId && enabled && !shouldSkipQuery,
  });

  return {
    isLoading,
    error,
    data: data as UserProfileSchema,
  };
};
